import { useEffect, useState, useRef } from "react";
import {
  getTaskCommentsApi,
  addTaskCommentApi,
} from '../../app/api'
import { MessageCircle, Send } from "lucide-react";

export default function TaskComments({ taskId }) {
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  /* ================= FETCH COMMENTS ================= */
  useEffect(() => {
    if (!taskId) return;

    setLoading(true);
    getTaskCommentsApi(taskId)
      .then((res) => setComments(res.data || []))
      .finally(() => setLoading(false));
  }, [taskId]);

  /* ================= AUTO SCROLL ================= */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments]);

  /* ================= ADD COMMENT ================= */
  const addComment = async () => {
    if (!comment.trim()) return;

    const res = await addTaskCommentApi(taskId, {
      comment: comment.trim(),
    });

    if (res.success) {
      const refreshed = await getTaskCommentsApi(taskId);
      setComments(refreshed.data || []);
      setComment("");
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-400 shadow-sm">
        Loading comments…
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-black uppercase tracking-[0.14em] text-slate-700">Comments</h3>
          <p className="mt-1 text-xs font-medium text-slate-400">Keep the conversation attached to this card</p>
        </div>
        <MessageCircle size={18} className="text-blue-500" />
      </div>

      {/* ===== COMMENTS LIST ===== */}
      <div className="max-h-[180px] space-y-2 overflow-y-auto pr-1">
        {comments.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm font-semibold italic text-slate-400">
            No comments yet
          </div>
        ) : (
          comments.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            >
              <div className="font-black text-slate-800">
                {c.user_name || "User"}
              </div>
              <div className="mt-1 text-slate-600">
                {c.comment}
              </div>
              <div className="mt-2 text-[11px] font-semibold text-slate-400">
                {new Date(c.created_at).toLocaleString()}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* ===== ADD COMMENT ===== */}
      <div className="mt-4 flex gap-2">
        <input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Write a comment…"
          className="h-10 min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
        />
        <button
          onClick={addComment}
          className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-blue-600 px-4 text-sm font-black text-white shadow-sm transition hover:bg-blue-700"
        >
          <Send size={15} />
          Send
        </button>
      </div>
    </section>
  );
}
